<script lang="ts">
    import { onMount } from "svelte";

    export let collapsedHeight = 160;
    export let expanded = false; // Default to collapsed
    export let hideExpandButton = false;
    export let cardContentWrapperHeight = 0;

    let contentRef: HTMLElement = null;
    let contentHeight = 0;
    let showButton = true;
    let maxCardHeight = "auto";

    function toggle() {
        expanded = !expanded;
    }

    $: cardContentWrapperHeight = expanded ? contentHeight : collapsedHeight;

    function measureHeight(node: HTMLElement) {
        // Set initial height
        contentHeight = node.offsetHeight;
        updateButtonVisibility();

        // Set up a resize observer to handle content changes
        const resizeObserver = new ResizeObserver(() => {
            contentHeight = node.offsetHeight;
            updateButtonVisibility();
        });

        resizeObserver.observe(node);

        // Cleanup observer when component is destroyed
        return {
            destroy() {
                resizeObserver.disconnect();
            },
        };
    }

    function updateButtonVisibility() {
        if (hideExpandButton) {
            showButton = false;
            return;
        }
        showButton = contentHeight > collapsedHeight;
    }

    function calculateMaxCardHeight() {
        if (hideExpandButton) {
            maxCardHeight = `${collapsedHeight}px`;
            return;
        }
        if (expanded) {
            maxCardHeight = `${contentHeight}px`;
            return;
        }
        maxCardHeight = showButton ? `${collapsedHeight}px` : "auto";
    }

    onMount(() => {
        if (contentRef) {
            calculateMaxCardHeight();
        }
    });

    $: hideExpandButton,
        expanded,
        contentHeight,
        showButton,
        calculateMaxCardHeight();
</script>

<div class="card" style="height: {maxCardHeight}">
    <div class="content" bind:this={contentRef} use:measureHeight>
        <slot></slot>
    </div>
</div>

<div
    class="collapsible"
    class:collapsible--inner-shadow={(showButton || hideExpandButton) && !expanded}
>
    <div class="footer-wrapper">
        <slot name="footer-content" />
    </div>
    {#if showButton}
        <button on:click={toggle} class="expand-button">
            {expanded ? "Collapse" : "Expand"}
            <svg
                width="10"
                height="10"
                class="arrow-icon"
                class:rotated={expanded}
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M9.66732 5.00001L8.84482 4.17751L5.58398 7.43251V0.333344H4.41732V7.43251L1.16232 4.17168L0.333984 5.00001L5.00065 9.66668L9.66732 5.00001Z"
                />
            </svg>
        </button>
    {/if}
</div>

<style>
    .collapsible {
        @apply relative flex gap-4 p-4 py-2 !important;
    }
    /* purgecss start ignore */
    .collapsible--inner-shadow::after {
        @apply absolute content-[""] top-0 z-40 -ml-4 -mt-4 block h-4 w-full bg-gradient-to-t from-white to-transparent !important;
    }
    /* purgecss end ignore */
    .footer-wrapper {
        @apply flex-grow !important;
    }
    .card {
        @apply overflow-hidden transition-all !important;
    }
    .expand-button {
        @apply reset flex shrink-0 cursor-pointer items-center gap-2 fill-gray-500 text-xs text-gray-500 transition-colors hover:fill-black hover:text-black !important;
    }
    .arrow-icon {
        @apply fill-inherit !important;
    }
    .arrow-icon.rotated {
        @apply rotate-180 !important;
    }
</style>
